import React, { createContext, useContext, useState, useEffect } from 'react';
import request from '../utils/request';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 检查是否已认证
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token;
  };

  // 获取当前用户信息
  const getCurrentUser = async () => {
    try {
      const response = await request.get('/auth/me');
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return null;
    }
  };

  // 登录函数
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // 使用远程用户库的表单登录端点，和后台创建用户一致
      const form = new URLSearchParams();
      form.append('username', username);
      form.append('password', password);

      const response = await request.post('/auth/login', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      if (response.success && response.data) {
        const { access_token, token_type } = response.data;
        localStorage.setItem('token', access_token);
        
        // 获取完整的用户信息
        const userInfo = await getCurrentUser();
        if (userInfo) {
          setUser(userInfo);
        } else {
          // 如果获取用户信息失败，使用基本信息
          setUser({ username, id: null });
        }
        
        return true;
      } else {
        setError(response.message || '登录失败');
        return false;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || '登录失败';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 注册函数
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await request.post('/auth/register', userData);
      
      if (response.success && response.data) {
        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        setUser({ username: userData.username });
        return true;
      } else {
        setError(response.message || '注册失败');
        return false;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.detail || '注册失败';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 登出函数
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('currentTemplateTaskId');
    localStorage.removeItem('dataProcessingMode');
    // 语言偏好可保留；任务相关状态必须清掉，避免换账号后串台
    setUser(null);
  };

  // 初始化时检查token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // 获取用户信息
      getCurrentUser().then(userInfo => {
        if (userInfo) {
          setUser(userInfo);
        } else {
          // 如果获取用户信息失败，清除token
          localStorage.removeItem('token');
          setUser(null);
        }
      });
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};